import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import StickyReferCTA from "./StickyReferCTA";
import Footer from "./Footer";

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white text-ink-900">
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md transition-all ${
          scrolled ? "border-b border-slate-100 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
            <img
              src={SITE.logo}
              alt={`${SITE.name} logo`}
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className="font-serif text-xl tracking-tight text-ink-900">
              {SITE.name}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? "text-brand font-semibold" : "text-ink-700 hover:text-ink-900"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${SITE.phone.replace(/\s/g, "")}`}
              data-testid="header-phone"
              className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-brand transition-colors"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <Link
              to="/professionals"
              data-testid="header-refer-cta"
              className="inline-flex items-center rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Refer a Client
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -mr-2 text-ink-900"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white">
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-slate-100 text-base ${
                      isActive ? "text-brand font-semibold" : "text-ink-900"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/professionals"
                data-testid="mobile-refer-cta"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold"
              >
                Refer a Client
              </Link>
              <a
                href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                className="mt-2 inline-flex items-center justify-center gap-2 text-ink-700 py-2"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>
      <StickyReferCTA />
      <Footer />
    </div>
  );
}
